import React, { useEffect } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'

import './styles/main.scss'
import './styles/custom.css'
import './App.css'
import './v2/styles/main.css'

import { AuthenticatedRoute } from './shared/routing/AuthenticatedRoute'
import { Header } from './components/ui/Header'
import { Footer } from './components/ui/Footer'

import { LoginContainer } from './v2/login/LoginContainer'
import { DashboardContainer } from './components/dashboard/DashboardContainer'
import { ClientsContainer } from './components/clients/ClientsContainer'
import { useUserStore } from './shared/store/userStore'
import { SpinnerLoader } from './components/ui/SpinnerLoader'
import { ConcreteTenderListContainer } from './components/concrete-tender/ConcreteTenderListContainer'
import { TemplateTenderListContainer } from './components/template-tender/TemplateTenderListContainer'
import { InvoiceListContainer } from './components/invoice/InvoiceListContainer'
import { SignoutContainer } from './components/signout/SignoutContainer'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { InvoiceDetailsContainer } from './components/invoice/InvoiceDetailsContainer'
import { ConcreteTenderDetailsContainer } from './components/concrete-tender/ConcreteTenderDetailsContainer'
import { TemplateTenderDetailsContainer } from './components/template-tender/TemplateTenderDetailsContainer'
import { SettingsContainer } from './components/settings/SettingsContainer'
import { PriceListContainer } from './components/price-list/PriceListContainer'
import { ImageSelectionContainer } from './shared/documents/DocumentImageSelectionContainer'
import { ToastContainer } from 'react-toastify'
import { RegistrationContainer } from './v2/registration/RegistrationContainer'
import { ImpersonationContainer } from './components/impersonation/ImpersonationContainer'
import { ForgotPasswordContainer } from './v2/reset-password/ForgotPasswordContainer'
import { ResetPasswordContainer } from './v2/reset-password/ResetPasswordContainer'
import 'react-toastify/dist/ReactToastify.minimal.css'
import i18next from 'i18next'
import { useCompanyStore } from './shared/store/companyStore'
import { useOnboardingStore } from './shared/store/onboardingStore'

const UsetifulContainer = () => {
    const scriptId = 'usetifulScript'
    const user = useUserStore(state => state.user)

    useEffect(() => {
        const isProd = process.env.REACT_APP_QA === 'false'

        if (!isProd) return

        if (user.authenticated) {
            const companyType = user.CompanyType

            let segment: string = ''
            switch (companyType) {
                case '0':
                    segment = 'Basic'
                    break
                case '1':
                    segment = 'Lawyer'
                    break
                case '2':
                    segment = 'Window Maker'
                    break
                case '3':
                    segment = 'Renter'
                    break
                case '4':
                    segment = 'Other'
                    break
            }

            ;(window as any).usetifulTags = { userId: user.UserId, segment: segment, firstName: user.FirstName }

            const script = document.createElement('script')
            script.async = true
            script.src = 'https://www.usetiful.com/dist/usetiful.js'
            script.id = scriptId
            script.dataset.token = 'c33b8f968023e5e0c1b645ce3e40aa47'

            document.head.appendChild(script)
        } else {
            const el = document.getElementById(scriptId)

            if (el) el.remove()
            ;(window as any).usetifulTags = undefined
        }
    }, [user.authenticated, user.UserId, user.CompanyType, user.FirstName])

    return null
}

function App() {
    const user = useUserStore(state => state.user)
    const companyLoaded = useCompanyStore(state => state.companyLoaded)
    const loadCompany = useCompanyStore(state => state.loadCompany)

    const companySettings = useCompanyStore(state => state.companySettings)
    const updateLanguage = useUserStore(state => state.updateLanguage)
    const setCompanySettings = useCompanyStore(state => state.setCompanySettings)
    const initializeOnboarding = useOnboardingStore(state => state.initializeOnboarding)

    useEffect(() => {
        i18next.changeLanguage(user.UiLanguage)
    }, [user.UiLanguage])

    useEffect(() => {
        if (user.authenticated && !companyLoaded) {
            loadCompany()
        }
    }, [user.authenticated, companyLoaded, loadCompany])

    useEffect(() => {
        if (user.authenticated && companyLoaded && companySettings) {
            initializeOnboarding(companySettings, user.FirstName, user.LastName, updateLanguage, setCompanySettings)
        }
    }, [user, companyLoaded, companySettings, initializeOnboarding, updateLanguage, setCompanySettings])

    return (
        <>
            <ToastContainer />
            <UsetifulContainer />

            <Routes>
                <Route path="/impersonation" element={<ImpersonationContainer />} />

                <Route
                    path="/login"
                    element={
                        <div className="v2-root">
                            <LoginContainer />
                        </div>
                    }
                />
                <Route
                    path="/register"
                    element={
                        <div className="v2-root">
                            <RegistrationContainer />
                        </div>
                    }
                />
                <Route
                    path="/reset-password"
                    element={
                        <div className="v2-root">
                            <ResetPasswordContainer />
                        </div>
                    }
                />
                <Route
                    path="/forgot-password"
                    element={
                        <div className="v2-root">
                            <ForgotPasswordContainer />
                        </div>
                    }
                />

                <Route
                    path="/*"
                    element={
                        <>
                            <BreadcrumbsItem to="/">Izračunko</BreadcrumbsItem>

                            <SpinnerLoader />

                            <div className="push-footer" id="maindiv">
                                <Header />

                                <div ui-view="content">
                                    <Routes>
                                        <Route path="/dashboard" element={<AuthenticatedRoute element={<DashboardContainer />} />} />

                                        <Route path="/price-list" element={<AuthenticatedRoute element={<PriceListContainer />} />} />

                                        <Route path="/offers" element={<AuthenticatedRoute element={<ConcreteTenderListContainer />} />} />

                                        <Route
                                            path="/templates"
                                            element={<AuthenticatedRoute element={<TemplateTenderListContainer />} />}
                                        />

                                        <Route path="/clients/*" element={<AuthenticatedRoute element={<ClientsContainer />} />} />

                                        <Route path="/invoices" element={<AuthenticatedRoute element={<InvoiceListContainer />} />} />

                                        <Route
                                            path="/settings/*"
                                            element={<AuthenticatedRoute requiredRole="Administrator" element={<SettingsContainer />} />}
                                        />

                                        <Route path="/signout" element={<AuthenticatedRoute element={<SignoutContainer />} />} />

                                        <Route
                                            path="/template-offer/new"
                                            element={<AuthenticatedRoute element={<TemplateTenderDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/template-offer/new-from-offer"
                                            element={<AuthenticatedRoute element={<TemplateTenderDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/template-offer/new-from-invoice"
                                            element={<AuthenticatedRoute element={<TemplateTenderDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/template-offer/:tenderId"
                                            element={<AuthenticatedRoute element={<TemplateTenderDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/offer/new"
                                            element={<AuthenticatedRoute element={<ConcreteTenderDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/offer/:tenderId"
                                            element={<AuthenticatedRoute element={<ConcreteTenderDetailsContainer />} />}
                                        />

                                        <Route path="/invoice/new" element={<AuthenticatedRoute element={<InvoiceDetailsContainer />} />} />

                                        <Route
                                            path="/invoice/:invoiceId"
                                            element={<AuthenticatedRoute element={<InvoiceDetailsContainer />} />}
                                        />

                                        <Route
                                            path="/image-selection"
                                            element={<AuthenticatedRoute element={<ImageSelectionContainer />} />}
                                        />

                                        <Route
                                            path="/"
                                            element={
                                                user.authenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                                            }
                                        />
                                    </Routes>
                                </div>
                            </div>
                            <Footer />
                        </>
                    }
                />
            </Routes>
        </>
    )
}

export default App
